import React, { useState, useEffect, useRef } from 'react';
import { sendSms, SendSmsRequest, formatPhoneNumber, isValidUKPhone } from '../api/sms';
import { Claim } from '../api/claims';
import './SmsComposer.css';

interface SmsComposerProps {
  claim?: Claim;
  defaultPhone?: string;
  onClose: () => void;
  onSent?: (result: any) => void;
}

const MAX_SMS_LENGTH = 1600; // Maximum for concatenated SMS
const SINGLE_SMS_LENGTH = 160;

export default function SmsComposer({
  claim,
  defaultPhone = '',
  onClose,
  onSent
}: SmsComposerProps) {
  const [phoneNumber, setPhoneNumber] = useState(defaultPhone);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // Pre-populate phone number from claim
  useEffect(() => {
    if (claim && !defaultPhone) {
      // Get phone from claim data
      const phone = claim.phoneNumber || '';
      if (phone) {
        setPhoneNumber(phone);
      }
    }
  }, [claim, defaultPhone]);

  // Pre-fill with default message when component loads
  useEffect(() => {
    if (claim && !message) {
      const clientName = claim.firstName || claim.craFirstName || 'there';
      setMessage(
        `Hi ${clientName}, we have sent you an email regarding your Car Finance claim with us. ` +
        `Please check your emails to see this correspondence. Thanks, Complex Law`
      );
    }
  }, [claim]);

  const getClientName = (): string => {
    if (claim) {
      const firstName = claim.firstName || claim.craFirstName || '';
      return firstName || 'there';
    }
    return 'there';
  };

  const handleSend = async () => {
    // Validate phone number
    if (!phoneNumber) {
      setError('Please enter a phone number');
      return;
    }

    if (!isValidUKPhone(phoneNumber)) {
      setError('Please enter a valid UK mobile number');
      return;
    }

    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (message.length > MAX_SMS_LENGTH) {
      setError(`Message is too long (${message.length}/${MAX_SMS_LENGTH} characters)`);
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);

      const smsData: SendSmsRequest = {
        toNumber: formattedPhone,
        message: message.trim(),
        claimId: claim?.id
      };

      const result = await sendSms(smsData);

      if (result.success) {
        setSuccess('SMS sent successfully!');
        if (onSent) {
          onSent(result);
        }
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(result.error || 'Failed to send SMS');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const insertText = (text: string) => {
    const textarea = messageRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = message.substring(0, start) + text + message.substring(end);
    setMessage(newMessage);

    // Set cursor position after inserted text
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
      textarea.focus();
    }, 0);
  };

  const getCharCountClass = (): string => {
    if (message.length > MAX_SMS_LENGTH) return 'danger';
    if (message.length > SINGLE_SMS_LENGTH * 3) return 'warning';
    return '';
  };

  const getSmsSegments = (): number => {
    if (message.length === 0) return 0;
    if (message.length <= 160) return 1;
    // Concatenated SMS uses 153 chars per segment
    return Math.ceil(message.length / 153);
  };

  const phoneValid = phoneNumber ? isValidUKPhone(phoneNumber) : null;

  return (
    <div className="sms-composer-overlay">
      <div className="sms-composer">
        <div className="sms-composer-header">
          <h2>
            <span className="sms-icon">💬</span>
            Send SMS
            {claim && ` - ${claim.proclaimReference || claim.id}`}
          </h2>
          <button className="sms-close-button" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="sms-error-message">{error}</div>
        )}

        {success && (
          <div className="sms-success-message">{success}</div>
        )}

        <div className="sms-composer-body">
          <div className="sms-field">
            <label>Phone Number:</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="07xxx xxx xxx or +44..."
              disabled={sending}
            />
            <div className={`sms-phone-hint ${phoneValid === true ? 'sms-phone-valid' : phoneValid === false ? 'sms-phone-invalid' : ''}`}>
              {phoneValid === true && '✓ Valid UK mobile number'}
              {phoneValid === false && '✗ Enter a valid UK mobile number'}
              {phoneValid === null && 'UK mobile numbers starting with 07'}
            </div>
          </div>

          
          <div className="sms-field message-field">
            <label>Message:</label>
            {claim && (
              <div className="quick-insert-buttons">
                <button
                  type="button"
                  className="quick-insert-btn"
                  onClick={() => insertText(getClientName())}
                  disabled={sending}
                >
                  + Name
                </button>
                <button
                  type="button"
                  className="quick-insert-btn"
                  onClick={() => insertText(claim.proclaimReference || claim.id)}
                  disabled={sending}
                >
                  + Ref
                </button>
              </div>
            )}
            <textarea
              ref={messageRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={5}
              disabled={sending}
            />
            <div className="char-counter">
              <span className={`count ${getCharCountClass()}`}>
                {message.length} / {MAX_SMS_LENGTH} characters
              </span>
              <span className="segments">
                {getSmsSegments()} SMS segment{getSmsSegments() !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        <div className="sms-composer-footer">
          <div className="sms-info">
            Sent from ComplexLaw
          </div>
          <div className="sms-footer-buttons">
            <button
              onClick={onClose}
              disabled={sending}
              className="sms-cancel-button"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !phoneNumber || !message.trim()}
              className="sms-send-button"
            >
              {sending ? 'Sending...' : 'Send SMS'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
